import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ConfigProvider,
  DatePicker,
  Input,
  Select,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { collection, getDocs } from "firebase/firestore";
import SuperAdminContainer from "../SuperAdminContainer";
import { db } from "../../../services/firebaseService/connection";
import PackageTimelineModal from "./PackageTimelineModal.jsx";
import { ScreenHeader } from "../../../CommonComponents";
import {
  getDayjsFromTimestamp,
  sortObjectByNumber,
} from "../../../utils/helper";
const { RangePicker } = DatePicker;

const formatDate = (ts) => {
  const date = getDayjsFromTimestamp(ts);
  return date ? date.format("MM/DD/YYYY") : "—";
};

const SuperadminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const packagesFetchedRef = useRef(false);
  const [seasons, setSeasons] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    season: "all",
    status: "all",
    group: "all",
    autorenewal: "all",
    expiryRange: null,
  });

  const groupNameMap = useMemo(() => {
    if (!packages || packages.length === 0) return {};
    return packages.reduce((acc, pkg) => {
      if (pkg.groupId && pkg.groupName) {
        acc[pkg.groupId] = pkg.groupName;
      }
      return acc;
    }, {});
  }, [packages]);

  const groupOptions = useMemo(() => {
    if (!packages || packages.length === 0)
      return [{ label: "All groups", value: "all" }];

    const uniqueGroups = new Map();
    packages.forEach((pkg) => {
      if (pkg.groupId && !uniqueGroups.has(pkg.groupId)) {
        uniqueGroups.set(pkg.groupId, pkg.groupName || pkg.groupId);
      }
    });

    const options = Array.from(uniqueGroups.entries()).map(([id, name]) => ({
      label: name,
      value: id,
    }));

    const sortedOptions = options.sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    return [{ label: "All groups", value: "all" }, ...sortedOptions];
  }, [packages]);

  const getExpiryMs = (pkg) => {
    const date = getDayjsFromTimestamp(pkg?.packageExpiryDate);
    return date ? date.valueOf() : -Infinity;
  };

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const seasonsSet = new Set();
      const groupSnapshots = await getDocs(
        collection(db, "registration_form_layout")
      );

      const perGroupPromises = groupSnapshots.docs.map(async (groupDoc) => {
        const groupData = groupDoc.data();
        const groupName =
          groupData?.groupName || groupData?.name || groupDoc.id;

        const pkgSnap = await getDocs(
          collection(db, "registration_form_layout", groupDoc.id, "packages")
        );
        return pkgSnap.docs.map((pkgDoc) => {
          if (pkgDoc.data().season && !isNaN(pkgDoc.data().season)) {
            seasonsSet.add(Number(pkgDoc.data().season));
          }
          return {
            id: pkgDoc.id,
            groupId: groupDoc.id,
            groupName: groupName,
            ...pkgDoc.data(),
          };
        });
      });

      const results = await Promise.all(perGroupPromises);
      const allPackages = results.flat().filter((pkg) => {
        const expiry = pkg.packageExpiryDate;
        return (
          (typeof expiry === "number" || typeof expiry === "string") &&
          expiry &&
          !pkg.isDeleted
        );
      });
      setSeasons(
        Array.from(seasonsSet)
          .sort((a, b) => Number(b) - Number(a))
          .map((season) => ({
            label: String(season),
            value: String(season),
          }))
      );
      setPackages(allPackages);
    } catch (err) {
      console.error("Failed to fetch packages", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch packages once on mount
    if (!packagesFetchedRef.current) {
      packagesFetchedRef.current = true;
      fetchPackages();
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "Package",
        dataIndex: "name",
        key: "name",
        render: (value) => value || "—",
      },
      {
        title: "Group",
        dataIndex: "groupId",
        key: "groupName",
        sorter: (a, b) => {
          const nameA = groupNameMap[a.groupId] || a.groupId || "";
          const nameB = groupNameMap[b.groupId] || b.groupId || "";
          return nameA.localeCompare(nameB);
        },
        render: (groupId) => {
          const name = groupNameMap[groupId];
          return name || groupId || "—";
        },
      },
      {
        title: "Season",
        dataIndex: "season",
        key: "season",
        sorter: (a, b) => {
          const seasonA = a.season ?? 0;
          const seasonB = b.season ?? 0;
          return seasonA - seasonB;
        },
        render: (value) => value || "—",
      },
      {
        title: "Price",
        dataIndex: ["price", "amount"],
        key: "price",
        render: (value) => (value || value === 0 ? `$${value}` : "—"),
      },
      {
        title: "Auto Renewal",
        dataIndex: "offerAutorenewal",
        key: "offerAutorenewal",
        render: (value) => (value ? "Yes" : "No"),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        sorter: (a, b) => {
          const statusA = a.status || "";
          const statusB = b.status || "";
          return statusA.localeCompare(statusB);
        },
        render: (value) =>
          value ? (
            <Tag color={value === "active" ? "green" : "volcano"}>{value}</Tag>
          ) : (
            "—"
          ),
      },
      {
        title: (
          <Tooltip title="(mm/dd/yyyy)">
            <span>Expiry</span>
          </Tooltip>
        ),
        dataIndex: "packageExpiryDate",
        key: "packageExpiryDate",
        sorter: (a, b) => sortObjectByNumber(a, b, "packageExpiryDate"),
        render: (value) => formatDate(value),
      },
      {
        title: "Timeline",
        key: "timeline",
        render: (_, record) => (
          <a
            onClick={() => {
              setSelectedPackage(record);
              setDetailModalOpen(true);
            }}
            style={{ color: "#1890ff", textDecoration: "underline" }}
          >
            View
          </a>
        ),
      },
    ],
    [groupNameMap]
  );

  const applyFilters = useMemo(() => {
    const { season, search, status, group, autorenewal, expiryRange } = filters;
    const searchTerm = (search || "").trim().toLowerCase();
    return packages.filter((pkg) => {
      // search by package name
      const matchesSearch = searchTerm
        ? (pkg.name || "").toLowerCase().includes(searchTerm)
        : true;

      // season filter
      const matchesSeason =
        season === "all" || !season
          ? true
          : String(pkg.season) === String(season);

      // status filter
      const matchesStatus =
        status === "all" || !status ? true : pkg.status === status;

      // group filter
      const matchesGroup =
        group === "all" || !group ? true : pkg.groupId === group;

      // autorenewal filter
      const matchesAutorenewal =
        autorenewal === "all" || !autorenewal
          ? true
          : autorenewal === "yes"
            ? pkg.offerAutorenewal === true
            : pkg.offerAutorenewal !== true;

      // expiry range filter
      let matchesExpiry = true;
      if (expiryRange && expiryRange.length === 2) {
        const [start, end] = expiryRange;
        const startTs = start?.startOf("day").valueOf();
        const endTs = end?.endOf("day").valueOf();
        const pkgDate = getDayjsFromTimestamp(pkg.packageExpiryDate);
        if (pkgDate) {
          const pkgDateMs = pkgDate.valueOf();
          matchesExpiry = pkgDateMs >= startTs && pkgDateMs <= endTs;
        } else {
          matchesExpiry = false;
        }
      }

      return (
        matchesSeason &&
        matchesSearch &&
        matchesStatus &&
        matchesGroup &&
        matchesAutorenewal &&
        matchesExpiry
      );
    });
  }, [filters, packages]);

  const sortedAndFilteredPackages = useMemo(() => {
    const sorted = [...applyFilters].sort((a, b) => {
      // First sort by expiry date (descending - latest first)
      const expiryA = getExpiryMs(a);
      const expiryB = getExpiryMs(b);
      if (expiryB !== expiryA) return expiryB - expiryA;
      // Both null or both exist with same value - sort by group name (ascending)
      const nameA = groupNameMap[a.groupId] || a.groupId || "";
      const nameB = groupNameMap[b.groupId] || b.groupId || "";
      return nameA.localeCompare(nameB);
    });
    return sorted;
  }, [applyFilters, groupNameMap]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <SuperAdminContainer>
      <ScreenHeader
        title="Packages"
        elementCount={sortedAndFilteredPackages.length}
      />

      <div
        className="superadmin-packages-filters"
        style={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "white",
          padding: "8px 12px",
          marginTop: "2px",
        }}
      >
        <div>
          <Input
            placeholder="Search packages"
            allowClear
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            style={{ width: 220 }}
          />
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <Select
            placeholder="Season"
            value={filters.season}
            onChange={(value) => handleFilterChange("season", value)}
            style={{ width: 180 }}
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            options={[{ label: "All seasons", value: "all" }, ...seasons]}
          />
          <Select
            placeholder="Group"
            value={filters.group}
            popupMatchSelectWidth={false}
            onChange={(value) => handleFilterChange("group", value)}
            style={{ width: 180 }}
            showSearch
            optionFilterProp="label"
            options={groupOptions}
          />
          <Select
            placeholder="Status"
            value={filters.status}
            popupMatchSelectWidth={false}
            onChange={(value) => handleFilterChange("status", value)}
            style={{ width: 160 }}
            options={[
              { label: "All statuses", value: "all" },
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
            ]}
          />
          <Select
            placeholder="Auto Renewal"
            value={filters.autorenewal}
            popupMatchSelectWidth={false}
            onChange={(value) => handleFilterChange("autorenewal", value)}
            style={{ width: 160 }}
            options={[
              { label: "All renewals", value: "all" },
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ]}
          />
          <RangePicker
            value={filters.expiryRange}
            onChange={(value) => handleFilterChange("expiryRange", value)}
            allowClear
            placeholder={["Expiry start", "Expiry end"]}
          />
        </div>
      </div>

      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBorderRadius: 0,
            },
          },
        }}
      >
        <Table
          bordered
          columns={columns}
          loading={loading}
          dataSource={sortedAndFilteredPackages.map((pkg) => ({
            key: `${pkg.groupId}-${pkg.id}`,
            ...pkg,
          }))}
          pagination={{
            defaultPageSize: 100,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
          }}
          scroll={{ x: "max-content" }}
        />
      </ConfigProvider>

      <PackageTimelineModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        pkg={selectedPackage}
      />
    </SuperAdminContainer>
  );
};

export default SuperadminPackages;
